import React from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from '@mui/material/styles';
import { configStore } from "../store";
import { theme } from "./theme";
import { PublicStepLayout, SessionExpiredHandler, ParamRedirect } from "../components";
import "../utils/fingerprint";

const Home = React.lazy(() => import("../features/home/presentation/pages/Home"));
const Signin = React.lazy(() => import("../features/signin/presentation/pages/Signin"));
const Signup = React.lazy(() => import("../features/signup/presentation/pages/Signup"));
const Calendar = React.lazy(() => import("../features/schedules/presentation/pages/Calendar"));
const Schedules = React.lazy(() => import("../features/schedules/presentation/pages/Schedules"));
const Patients = React.lazy(() => import("../features/patients/presentation/pages/Patients"));
const Suppliers = React.lazy(() => import("../features/suppliers/presentation/pages/Suppliers"));
const Jobs = React.lazy(() => import("../features/jobs/presentation/pages/Jobs"));
const Payments = React.lazy(() => import("../features/payments/presentation/pages/Payments"));
const Cashier = React.lazy(() => import("../features/cashier/presentation/pages/Cashier"));
const Charges = React.lazy(() => import("../features/charges/presentation/pages/Charges"));
const Users = React.lazy(() => import("../features/users/presentation/pages/Users"));
const Reports = React.lazy(() => import("../features/reports/presentation/pages/Reports"));
const Entries = React.lazy(() => import("../features/reports/presentation/pages/Entries"));
const Dependents = React.lazy(() => import("../features/reports/presentation/pages/Dependents"));
const CalendarClient = React.lazy(() => import("../features/clients/presentation/pages/Calendar"));
const SelectHour = React.lazy(() => import("../features/clients/presentation/pages/SelectHour"));
const ScheduleDone = React.lazy(() => import("../features/clients/presentation/pages/ScheduleDone"));
const PatientSchedules = React.lazy(() => import("../features/clients/presentation/pages/PatientSchedules"));
const Welcome = React.lazy(() => import("../features/welcome/presentation/pages/Welcome"));
const About = React.lazy(() => import("../features/about/presentation/pages/About"));
const Messenger = React.lazy(() => import("../features/messenger/presentation/pages/Messenger"));
const Subscription = React.lazy(() => import("../features/subscription/presentation/pages/Subscription"));
const PhoneValidate = React.lazy(() => import("../features/signup/presentation/pages/PhoneValidate"));
const SelectJob = React.lazy(() => import("../features/clients/presentation/pages/SelectJob"));
const ChangePassword = React.lazy(() => import("../features/change-password/presentation/pages/ChangePassword"));
const Dashboard = React.lazy(() => import("../features/dashboard/presentation/pages/Dashboard"));

const store = configStore();

export const App = () => (
  <Provider store={store}>
    <BrowserRouter>
      <React.Suspense fallback="Loading...">
        <ThemeProvider theme={theme}>
          <SessionExpiredHandler />
          <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="/sobre" element={<About />} />
            <Route path="/trocar-senha/:token" element={<ChangePassword />} />
            <Route path="/:clinic/entrar" element={<Signin />} />
            <Route path="/:clinic/assinatura" element={<Subscription />} />
            <Route path="/:clinic/agendar" element={<PublicStepLayout />}>
              <Route index element={<Signup />} />
              <Route path="servico" element={<SelectJob />} />
              <Route path="dia" element={<CalendarClient />} />
              <Route path="horario" element={<SelectHour />} />
              <Route path="concluido" element={<ScheduleDone />} />
            </Route>
            <Route path="/:clinic/me" element={<PublicStepLayout />}>
              <Route index element={<PatientSchedules />} />
              <Route path="validar" element={<PhoneValidate />} />
            </Route>
            <Route path="/:clinic/app" element={<Home />}>
              <Route index element={<Dashboard />} />
              <Route path="schedules" element={<Calendar />}>
                <Route path=":day" element={<Schedules />} />
              </Route>
              <Route path="patients" element={<Patients />} />
              <Route path="professionals" element={<Suppliers />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="payments" element={<Payments />} />
              <Route path="cashier" element={<Cashier />} />
              <Route path="charges" element={<Charges />} />
              <Route path="reports" element={<Reports />}>
                <Route index path="entries" element={<Entries />} />
                <Route index path="dependents" element={<Dependents />} />
              </Route>
              <Route path="users" element={<Users />} />
              <Route path="whatsapp" element={<Messenger />} />
            </Route>
            <Route path="/:clinic" element={<Navigate to="app" replace />} />
            <Route path="/signin/:clinic" element={<ParamRedirect to="/:clinic/entrar" />} />
            <Route path="/signup/:clinic" element={<ParamRedirect to="/:clinic/agendar" />} />
            <Route path="/schedule/:clinic" element={<ParamRedirect to="/:clinic/me" />} />
          </Routes>
        </ThemeProvider>
      </React.Suspense>
    </BrowserRouter>
  </Provider>
);
